import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';

/**
 * BackendHealthCheck Component
 *
 * Checks backend health on initial app load.
 * If backend returns 503 (staging is asleep), redirects to wake page.
 * Otherwise, renders the app normally.
 */
const BackendHealthCheck = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isHealthy, setIsHealthy] = useState(false);

  useEffect(() => {
    const checkBackendHealth = async () => {
      // Determine API URL based on current hostname
      let apiUrl;
      const hostname = window.location.hostname;

      if (hostname === 'staging.spark.rapidlearningcycles.com') {
        apiUrl = 'https://api-staging.spark.rapidlearningcycles.com';
      } else if (hostname === 'spark.rapidlearningcycles.com') {
        apiUrl = 'https://api.spark.rapidlearningcycles.com';
      } else {
        // Development or unknown - skip health check
        console.log('Skipping health check for development environment');
        setIsHealthy(true);
        setIsChecking(false);
        return;
      }

      console.log('Checking backend health at:', apiUrl);

      try {
        const response = await fetch(`${apiUrl}/api/v1/health/`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          },
          // Don't use credentials for health check (faster)
          credentials: 'omit'
        });

        console.log('Health check response status:', response.status);

        if (response.status === 503) {
          // Backend is asleep - redirect to wake page
          console.log('Backend is asleep (503), redirecting to wake page...');

          // Store current URL to return to after wake
          const returnUrl = window.location.pathname + window.location.search;
          sessionStorage.setItem('returnUrl', returnUrl);
          console.log('Stored returnUrl:', returnUrl);

          // Redirect to wake page
          console.log('Redirecting to /wakeup.html');
          window.location.href = '/wakeup.html';
          return;
        }

        if (response.ok) {
          // Backend is healthy
          setIsHealthy(true);
        } else {
          // Other error - let the app load and handle it
          console.warn(`Backend health check returned ${response.status}, allowing app to load`);
          setIsHealthy(true);
        }
      } catch (error) {
        // CORS error or network error usually means backend is down/asleep
        console.warn('Backend health check failed:', error.message);

        // If we get a CORS error, it likely means the ALB returned 503 without CORS headers
        // This happens when backend is asleep. Redirect to wake page.
        if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
          console.log('CORS error detected - backend is likely asleep, redirecting to wake page...');

          // Store current URL to return to after wake
          const returnUrl = window.location.pathname + window.location.search;
          sessionStorage.setItem('returnUrl', returnUrl);
          console.log('Stored returnUrl:', returnUrl);

          // Redirect to wake page
          console.log('Redirecting to /wakeup.html');
          window.location.href = '/wakeup.html';
          return;
        }

        // Other errors - let the app load and handle it
        console.log('Unknown error, allowing app to load');
        setIsHealthy(true);
      } finally {
        setIsChecking(false);
      }
    };

    checkBackendHealth();
  }, []);

  // Show loading spinner while checking health
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <p style={{ color: 'white', marginTop: 16 }}>Checking backend status...</p>
        </div>
      </div>
    );
  }

  // Backend is healthy (or we're allowing the app to load)
  if (isHealthy) {
    return children;
  }

  // Should never reach here, but just in case
  return null;
};

export default BackendHealthCheck;
